<script lang="ts">
  import { goto, invalidate } from '$app/navigation'
  import { resolve } from '$app/paths'
  import { page, updated } from '$app/state'
  import { createAndSetAuthContext } from '$lib/auth-context.svelte'
  import Footer from '$lib/components/Footer.svelte'
  import Header from '$lib/components/header/Header.svelte'
  import Notifications from '$lib/components/notifications/Notifications.svelte'
  import Breadcrumb from '$lib/components/ui/Breadcrumb.svelte'
  import RewardRevealHost from '$lib/reveal/RewardRevealHost.svelte'
  import { createModContext } from '$lib/stores/movement-of-the-day.svelte.js'
  import { setSupabaseContext } from '$lib/supabase/context.js'
  import { inMaintenanceMode } from '$lib/utils/maintenance.js'
  import { onMount, type Snippet } from 'svelte'
  import { toStore } from 'svelte/store'
  import { fade, scale } from 'svelte/transition'
  import GTag from './GTag.svelte'
  import type { TypedSupabaseClient } from '$lib/supabase'
  import type { PageDataSession } from '$lib/types'
  import { addCurrent } from '$lib/stores/add-current.svelte'

  let {
    supabase,
    session,
    children,
  }: {
    supabase: TypedSupabaseClient
    session: PageDataSession | null
    children?: Snippet
  } = $props()

  setSupabaseContext({ supabase: addCurrent(toStore(() => supabase)) })

  const dependencies = {
    get supabase() {
      return supabase
    },
    get timezone() {
      return session?.settings.timezone
    },
    get session() {
      return session
    },
  }

  createModContext(dependencies)

  createAndSetAuthContext(dependencies)

  let didSetup = false

  const setup = () => {
    if (didSetup) return
    didSetup = true
    setupSupabase()
  }

  const setupSupabase = async () => {
    supabase.auth.onAuthStateChange((event) => {
      if (event !== 'INITIAL_SESSION') {
        invalidate('supabase:auth')
      }
      if (event === 'PASSWORD_RECOVERY') {
        goto(resolve('/reset-password/set'))
      }
    })
  }

  onMount(() => {
    document.body.classList.add('hydrated')
    setup()
  })

  let successMessage = $state('')
  let errorMessage = $state('')

  $effect(() => {
    const url = page.url
    successMessage = url.searchParams.get('success') ?? ''
    errorMessage =
      url.searchParams.get('error') ??
      // Supabase errors
      new URLSearchParams(window.location.hash.slice(1)).get(
        'error_description',
      ) ??
      ''
    const timeoutId = window.setTimeout(() => {
      successMessage = ''
      errorMessage = ''
    }, 10000)
    return () => window.clearTimeout(timeoutId)
  })
</script>

<GTag />

<RewardRevealHost />

<div
  data-sveltekit-reload={updated.current ? '' : 'off'}
  class="layout"
  data-sveltekit-preload-data={inMaintenanceMode() ? 'off' : undefined}
>
  <Header />

  {#if successMessage || errorMessage}
    <div class="message" in:scale={{ duration: 300, start: 0.8 }} out:fade>
      {#if successMessage}
        <div class="success-message">{successMessage}</div>
      {/if}
      {#if errorMessage}
        <div class="error-message">{errorMessage}</div>
      {/if}
    </div>
  {/if}

  <main>
    <Breadcrumb />
    {@render children?.()}
  </main>

  <Footer />
</div>

<Notifications />

<style lang="postcss">
  .layout {
    --_main-padding-bottom: 11rem;
    display: grid;
    position: relative;
    grid-template-columns: auto;
    min-height: 100vh;
    overflow-x: hidden;

    @supports (overflow-x: clip) {
      overflow-x: clip;
    }
    & :global(> *) {
      min-width: 0;
    }
  }
  main {
    padding-top: 0;
    padding-bottom: var(--_main-padding-bottom);
    min-height: calc(100vh - var(--size-header-height));
  }
  .message {
    display: flex;
    position: fixed;
    top: 7rem;
    right: 0;
    left: 0;
    flex-direction: column;
    gap: 1rem;
    z-index: var(--layer-top);
    padding-inline: var(--size-content-px);
    pointer-events: none;
    & .success-message,
    & .error-message {
      position: relative;
      margin-inline: auto;
      border-radius: var(--radius-sm);
      padding: 1rem 2rem;
      pointer-events: all;
      text-align: center;
    }
    & .success-message {
      background-color: var(--color-bg-success-secondary);
      color: var(--color-text-success);
    }
    & .error-message {
      background-color: var(--color-bg-danger-secondary);
      color: var(--color-text-danger);
    }
  }
</style>
